import type { ProduktFreigabe, ProduktKey } from "@nl/shared"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  produktFreigabeControllerEigeneOptions,
  produktFreigabeControllerEigeneQueryKey,
  produktFreigabeControllerListOptions,
  produktFreigabeControllerListQueryKey,
  produktFreigabeControllerSetMutation,
  tenantsControllerAuditLogQueryKey,
} from "@/client/@tanstack/react-query.gen"
import { formatFreigabeAb, useRefetchZumTermin } from "@/lib/einheit-freigabe"
import { availableProduct } from "@/lib/product-media"
import { invalidateGruppenFreigaben } from "@/lib/team-queries"

/** The caller's own Sparkasse, for the product tiles and the picker. */
export function useProduktFreigaben() {
  return useQuery(produktFreigabeControllerEigeneOptions())
}

export function useTenantProduktFreigaben(tenantId: string) {
  return useQuery(
    produktFreigabeControllerListOptions({ path: { id: tenantId } })
  )
}

export function useSetProduktFreigabe(tenantId: string) {
  const client = useQueryClient()
  return useMutation({
    ...produktFreigabeControllerSetMutation(),
    onSuccess: (data) => {
      client.setQueryData(
        produktFreigabeControllerListQueryKey({ path: { id: tenantId } }),
        data
      )
      // The platform role may be switched into this very tenant.
      void client.invalidateQueries({
        queryKey: produktFreigabeControllerEigeneQueryKey(),
      })
      void client.invalidateQueries({
        queryKey: tenantsControllerAuditLogQueryKey(),
      })
      invalidateGruppenFreigaben(client)
    },
  })
}

/** The release state of every product for the caller's Sparkasse and group. */
export function useProduktFreigabeStatus() {
  const query = useProduktFreigaben()
  const liste = query.data
  useRefetchZumTermin(liste, query.refetch)

  const nachProdukt = new Map<ProduktKey, ProduktFreigabe>(
    (liste ?? []).map((p) => [p.produkt, p])
  )
  return {
    isLoading: query.isLoading,
    status: (produkt: ProduktKey) => nachProdukt.get(produkt),
    // Like the areas, an unknown state counts as open, so the tiles do not
    // flash locks on load. The static `available` already covers products
    // without material, and the server guards every start.
    istGesperrt: (produkt: ProduktKey) => {
      const status = nachProdukt.get(produkt)?.status
      return status === "gesperrt" || status === "geplant"
    },
  }
}

/**
 * The short badge for a locked product, or undefined when it can be started:
 * "Gesperrt", or the date a scheduled release opens it.
 */
export function useProduktSperre() {
  const { status, istGesperrt } = useProduktFreigabeStatus()
  return (produkt: ProduktKey): string | undefined => {
    if (!istGesperrt(produkt)) return undefined
    const ab = status(produkt)?.freigabeAb
    return ab ? `ab ${formatFreigabeAb(ab)}` : "Gesperrt"
  }
}

/**
 * The product a training page opens with: one with material that is unlocked,
 * or any product with material when an existing conversation is reopened,
 * because a lock only stops starting. The shell and the page must agree, so
 * both ask this.
 */
export function useTrainierbaresProdukt(
  key: string | undefined,
  gespraech: string | undefined
) {
  const { istGesperrt } = useProduktFreigabeStatus()
  const produkt = availableProduct(key)
  if (!produkt) return undefined
  return gespraech || !istGesperrt(produkt.key) ? produkt : undefined
}
